import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../config/firebase";

const joinWorkspaceFn = httpsCallable(getFunctions(getApp(), "us-central1"), "joinWorkspace");

export default function JoinWorkspace() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get("token");

  const [user, setUser]         = useState(undefined);
  const [code, setCode]         = useState("");
  const [status, setStatus]     = useState("idle"); // idle | loading | requested | error
  const [errorMsg, setErrorMsg] = useState("");
  const [wsName, setWsName]     = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u ?? null));
    return unsub;
  }, []);

  // Auto-submit if token is in URL
  useEffect(() => {
    if (tokenFromUrl && user) handleJoinByToken(tokenFromUrl);
  }, [tokenFromUrl, user]);

  // All membership writes happen server-side (admin SDK) so security rules can
  // stay strict — the client only forwards the invite token/code.
  const submitJoin = async (payload) => {
    setStatus("loading"); setErrorMsg("");
    try {
      const { data } = await joinWorkspaceFn(payload);
      setWsName(data?.name ?? "the workspace");
      setStatus("joined");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.message || "This invite is invalid or has been regenerated.");
    }
  };

  const handleJoinByToken = (token) => submitJoin({ token });

  const handleJoinByCode = () => {
    if (code.trim().length !== 6) { setErrorMsg("Enter a valid 6-digit code."); return; }
    submitJoin({ code: code.trim() });
  };

  // ── UI ────────────────────────────────────────────────────────────
  const wrap = (content) => (
    <div style={{
      minHeight: "100vh", background: "#060d18", display: "flex",
      alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Sans',sans-serif", padding: "2rem",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Syne:wght@700;800&family=DM+Mono:wght@500&display=swap" rel="stylesheet" />
      <div style={{
        background: "#111827", borderRadius: 18, padding: "2.5rem",
        maxWidth: 420, width: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.5)", textAlign: "center",
      }}>
        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#22c55e", marginBottom: "1.75rem", fontFamily: "'Syne',sans-serif" }}>
          Greenline
        </div>
        {content}
      </div>
    </div>
  );

  if (user === undefined || status === "loading") return wrap(
    <p style={{ color: "#9ca3af" }}>Loading…</p>
  );

  if (!user) return wrap(<>
    <p style={{ color: "#f9fafb", fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.5rem" }}>You've been invited</p>
    <p style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "1.5rem", lineHeight: 1.6 }}>
      Sign in or create an account first, then come back to this link to request access.
    </p>
    <button onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)} style={{
      background: "#22c55e", color: "white", border: "none", borderRadius: 10,
      padding: "0.8rem 1.75rem", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem", width: "100%",
    }}>Sign in to continue →</button>
  </>);

  if (status === "joined") return wrap(<>
    <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>✓</div>
    <p style={{ color: "#22c55e", fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.5rem" }}>You're in!</p>
    <p style={{ color: "#9ca3af", fontSize: "0.875rem", lineHeight: 1.6, marginBottom: "1.5rem" }}>
      You've joined <strong style={{ color: "#f9fafb" }}>{wsName}</strong>.
    </p>
    <button onClick={() => navigate("/app")} style={{
      background: "#22c55e", color: "white", border: "none", borderRadius: 10,
      padding: "0.8rem 1.75rem", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem", width: "100%",
    }}>Open Greenline →</button>
  </>);

  if (status === "error") return wrap(<>
    <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>✕</div>
    <p style={{ color: "#f87171", fontWeight: 700, fontSize: "1rem", marginBottom: "0.5rem" }}>Something went wrong</p>
    <p style={{ color: "#9ca3af", fontSize: "0.875rem", lineHeight: 1.6, marginBottom: "1.25rem" }}>{errorMsg}</p>
    <button onClick={() => { setStatus("idle"); setErrorMsg(""); setCode(""); }} style={{
      background: "transparent", border: "1px solid #374151", borderRadius: 10,
      padding: "0.65rem 1.5rem", color: "#9ca3af", cursor: "pointer", fontSize: "0.875rem",
    }}>Try again</button>
  </>);

  // Default: code entry form (shown when no token in URL)
  return wrap(<>
    <p style={{ color: "#f9fafb", fontWeight: 700, fontSize: "1.1rem", marginBottom: "0.4rem" }}>Join a workspace</p>
    <p style={{ color: "#9ca3af", fontSize: "0.875rem", marginBottom: "1.75rem", lineHeight: 1.6 }}>
      Enter the 6-digit code your admin shared with you.
    </p>
    <input
      style={{
        background: "#1f2937", border: "1px solid #374151", borderRadius: 10,
        padding: "0.85rem 1rem", color: "#f9fafb", fontSize: "1.4rem", outline: "none",
        width: "100%", boxSizing: "border-box", textAlign: "center",
        fontFamily: "'DM Mono',monospace", letterSpacing: "0.3em", marginBottom: "0.75rem",
      }}
      placeholder="000000"
      maxLength={6}
      value={code}
      onChange={e => { setCode(e.target.value.replace(/\D/g, "")); setErrorMsg(""); }}
      onKeyDown={e => e.key === "Enter" && handleJoinByCode()}
    />
    {errorMsg && (
      <p style={{ color: "#f87171", fontSize: "0.85rem", marginBottom: "0.75rem" }}>{errorMsg}</p>
    )}
    <button onClick={handleJoinByCode} disabled={code.length !== 6} style={{
      background: code.length === 6 ? "#22c55e" : "#1f2937",
      color: code.length === 6 ? "white" : "#4b5563",
      border: "none", borderRadius: 10, padding: "0.8rem",
      fontWeight: 700, cursor: code.length === 6 ? "pointer" : "not-allowed",
      fontSize: "0.95rem", width: "100%", transition: "all 0.2s",
    }}>
      Request to join →
    </button>
    <p style={{ color: "#4b5563", fontSize: "0.8rem", marginTop: "1.25rem" }}>
      Got a link instead?{" "}
      <span style={{ color: "#9ca3af" }}>Just open it in your browser.</span>
    </p>
  </>);
}